import { faPlus, faStar, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { Button, Input, Select, Textarea, Toggle } from '@/ui';

import { PATTERN_WIDGETS, type AutocompleteModel, type FieldDef, type FieldOption } from '../types';

import { AUTOCOMPLETE_MODELS, autocompleteModelLabel, fieldTypeMeta } from './fieldTypes';
import type { BuilderState } from './useBuilderState';

interface FieldSettingsPanelProps {
  state: BuilderState;
  onToggleReusable: (field: FieldDef) => void;
}

const hasOptions = (field: FieldDef) => field.widget === 'select' || field.widget === 'radio';

// The backend refuses a form whose pattern doesn't compile; say so while it's
// being typed instead of on save.
const patternError = (pattern: string | undefined) => {
  if (!pattern) return undefined;
  try {
    new RegExp(pattern);
    return undefined;
  } catch {
    return 'Not a valid regular expression.';
  }
};

const FieldSettingsPanel = ({ state, onToggleReusable }: FieldSettingsPanelProps) => {
  const field = state.fields.find((f) => f.key === state.selectedFieldKey) ?? null;
  // A rejected key never reaches the builder state, so the panel holds it here
  // to keep what was typed on screen next to the error. `owner` scopes the
  // draft to one field, clearing it when the selection moves on.
  const [keyDraft, setKeyDraft] = useState<{ owner: string; value: string } | null>(null);

  if (!field) {
    return (
      <div className="flex min-h-0 flex-col border-l border-uniform-gray-100 dark:border-uniform-gray-700">
        <div className="border-b border-uniform-gray-100 px-[18px] py-4 dark:border-uniform-gray-700">
          <span className="text-[13.5px] font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
            Field settings
          </span>
        </div>
        <p className="p-[18px] text-[13px] text-uniform-gray-400 dark:text-uniform-gray-500">
          Select a field on the canvas to edit it.
        </p>
      </div>
    );
  }

  const set = (patch: Partial<FieldDef>) => state.updateField(field.key, patch);
  const setOptions = (options: FieldOption[]) => set({ options });

  const draftKey = keyDraft?.owner === field.key ? keyDraft.value : null;
  const keyError =
    draftKey === null
      ? undefined
      : draftKey === ''
        ? 'Key is required.'
        : 'This key is already used on this form.';

  // Keys double as identity, so a duplicate would fuse two questions into one.
  // Typing through a prefix that already exists — "loc" on the way to
  // "location" — is the common way to hit this, so the key only commits once
  // it is free.
  const changeKey = (value: string) => {
    const taken = state.fields.some((f) => f !== field && f.key === value);
    if (taken || value === '') {
      setKeyDraft({ owner: field.key, value });
      return;
    }
    setKeyDraft(null);
    set({ key: value });
  };

  return (
    <div className="flex min-h-0 flex-col border-l border-uniform-gray-100 dark:border-uniform-gray-700">
      <div className="flex items-center justify-between border-b border-uniform-gray-100 px-[18px] py-4 dark:border-uniform-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
            Field settings
          </span>
          <span className="rounded bg-uniform-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300">
            {fieldTypeMeta(field.widget).badge}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-[18px]">
        <div className="flex flex-col gap-4">
          <Input
            label="Label"
            value={field.label}
            onChange={(e) => set({ label: e.target.value })}
          />
          <div>
            <Input
              label="Key"
              value={draftKey ?? field.key}
              error={keyError}
              // A linked field's key is the library question's handle, and the
              // backend rejects a form that renames it. Unstar the field first
              // to make it this form's own and free the key.
              disabled={!!field.inLibrary}
              onChange={(e) => changeKey(e.target.value)}
            />
            {!keyError && (
              <p className="mt-1.5 text-[11.5px] leading-snug text-uniform-gray-400 dark:text-uniform-gray-500">
                {field.inLibrary
                  ? 'Set by the library question this field is linked to.'
                  : 'Identifier used to store answers. Changing it orphans existing answers.'}
              </p>
            )}
          </div>
          <Textarea
            id="field-description"
            label="Description"
            rows={2}
            value={field.description ?? ''}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="Optional sub-label shown below the field"
          />

          {field.widget === 'html_content' && (
            <Textarea
              id="field-body"
              label="Text to read"
              rows={4}
              value={field.body ?? ''}
              onChange={(e) => set({ body: e.target.value })}
              placeholder="Content shown to the person filling the form"
              helpText="Required. This block is saved with the form."
            />
          )}

          {field.widget === 'image' && (
            <div>
              <Input
                label="Image URL"
                value={field.url ?? ''}
                placeholder="https://…"
                onChange={(e) => set({ url: e.target.value })}
              />
              <p className="mt-1.5 text-[11.5px] leading-snug text-uniform-gray-400 dark:text-uniform-gray-500">
                Required. Must be an http(s) link.
              </p>
            </div>
          )}

          {field.widget === 'autocomplete' && (
            <Select
              label="Search"
              helpText="The records this field searches when someone fills the form."
              options={AUTOCOMPLETE_MODELS.map((model) => ({
                value: model.value,
                label: model.label
              }))}
              selectedOption={{
                value: field.model ?? 'staff',
                label: autocompleteModelLabel(field.model)
              }}
              clearable={false}
              onChange={(option) => option && set({ model: option.value as AutocompleteModel })}
            />
          )}

          <div className="flex items-center justify-between rounded-lg border border-uniform-gray-100 bg-uniform-gray-50 px-3 py-2.5 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
            <span className="text-[13px] font-semibold text-uniform-gray-800 dark:text-uniform-gray-200">
              Required
            </span>
            <Toggle
              size="sm"
              color="primary"
              checked={field.required}
              onChange={(checked) => set({ required: checked })}
            />
          </div>

          {(field.widget === 'text' ||
            field.widget === 'textarea' ||
            field.widget === 'number' ||
            field.widget === 'select' ||
            field.widget === 'autocomplete') && (
            <Input
              label="Placeholder"
              value={field.placeholder ?? ''}
              placeholder="e.g. Enter value"
              onChange={(e) => set({ placeholder: e.target.value })}
            />
          )}

          {PATTERN_WIDGETS.includes(field.widget) && (
            <div className="flex flex-col gap-2">
              <Input
                label="Validation pattern"
                value={field.pattern ?? ''}
                placeholder="e.g. ^\d{3}-\d{4}$"
                inputClassName="font-mono"
                error={patternError(field.pattern)}
                helpText="Optional regular expression. Answers must match it before the task can be marked Done."
                onChange={(e) => set({ pattern: e.target.value || undefined })}
              />
              {field.pattern && (
                <Input
                  label="Pattern error message"
                  value={field.patternMessage ?? ''}
                  placeholder={`${field.label || field.key} is not in the expected format`}
                  onChange={(e) => set({ patternMessage: e.target.value || undefined })}
                />
              )}
            </div>
          )}

          {hasOptions(field) && (
            <div>
              <div className="mb-1.5 text-[12.5px] font-semibold text-uniform-gray-700 dark:text-uniform-gray-300">
                Options
              </div>
              <div className="flex flex-col gap-2">
                {(field.options ?? []).map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      id={`field-option-${index}`}
                      label={`Option ${index + 1}`}
                      hideLabel
                      className="flex-1"
                      value={option.label}
                      onChange={(e) => {
                        const next = [...(field.options ?? [])];
                        next[index] = { ...option, label: e.target.value };
                        setOptions(next);
                      }}
                    />
                    <Button
                      size="icon-sm"
                      color="destructive"
                      variant="quiet"
                      aria-label={`Remove option ${index + 1}`}
                      onClick={() =>
                        setOptions((field.options ?? []).filter((_, i) => i !== index))
                      }>
                      <FontAwesomeIcon icon={faTrashCan} className="text-xs" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="xs"
                  color="primary"
                  variant="ghost"
                  className="self-start"
                  onClick={() => {
                    const next = [...(field.options ?? [])];
                    const n = next.length + 1;
                    next.push({ value: `option_${n}`, label: `Option ${n}` });
                    setOptions(next);
                  }}>
                  <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                  Add option
                </Button>
              </div>
            </div>
          )}

          <div className="h-px bg-uniform-gray-100 dark:bg-uniform-gray-700" />

          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faStar}
                  className={
                    field.inLibrary
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-uniform-gray-300 dark:text-uniform-gray-600'
                  }
                />
                <span className="text-[13px] font-bold text-uniform-gray-800 dark:text-uniform-gray-200">
                  Reusable question
                </span>
              </div>
              <Toggle
                size="sm"
                color="primary"
                checked={!!field.inLibrary}
                onChange={() => onToggleReusable(field)}
              />
            </div>
            <p className="text-[11.5px] leading-relaxed text-uniform-gray-500 dark:text-uniform-gray-400">
              Saves this question to the shared Question Library so it can be dropped into any form.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-uniform-gray-100 px-[18px] py-3.5 dark:border-uniform-gray-700">
        <Button
          size="sm"
          color="destructive"
          variant="subtle"
          className="w-full"
          onClick={() => state.removeField(field.key)}>
          Remove field
        </Button>
      </div>
    </div>
  );
};

export default FieldSettingsPanel;
